import {metadaApi} from '@dms/api/base';

import {normalizeFastTrackItems, normalizeFastTrackIssues} from './normalizeFastTrack';
import {sliceReceiveNoteInfo, sliceReceiveNoteState} from './normalizeReceiveNote';
import {RECEIVE_NOTE_ALL_TAGS} from './tags';
import {
  BulkDeleteReceiveNoteRequest,
  BulkDeleteReceiveNoteRequestSchema,
  BulkDeleteReceiveNoteResponse,
  BulkDeleteReceiveNoteResponseSchema,
  GetReceiveNoteFastTrackRequest,
  GetReceiveNoteFastTrackRequestSchema,
  GetReceiveNoteFastTrackResponse,
  GetReceiveNoteFastTrackResponseSchema,
  GetReceiveNoteRequest,
  GetReceiveNoteRequestSchema,
  GetReceiveNoteResponse,
  GetReceiveNoteResponseSchema,
  NormalizedFastTrackItems,
  NormalizedFastTrackIssues,
  PatchReceiveNoteBulkEditRequest,
  PatchReceiveNoteBulkEditRequestSchema,
  PatchReceiveNoteBulkEditResponse,
  PatchReceiveNoteBulkEditResponseSchema,
  PatchReceiveNoteCompletedRequest,
  PatchReceiveNoteCompletedRequestSchema,
  PatchReceiveNoteCompletedResponse,
  PatchReceiveNoteCompletedResponseSchema,
  PatchReceiveNoteManualRequest,
  PatchReceiveNoteManualRequestSchema,
  PatchReceiveNoteManualResponse,
  PatchReceiveNoteManualResponseSchema,
  PatchReceiveNoteRequest,
  PatchReceiveNoteRequestSchema,
  PatchReceiveNoteResponse,
  PatchReceiveNoteResponseSchema,
  PatchReceiveNoteStateRequest,
  PatchReceiveNoteStateRequestSchema,
  PatchReceiveNoteStateResponse,
  PatchReceiveNoteStateResponseSchema,
  PostReceiveNoteManualRequest,
  PostReceiveNoteManualRequestSchema,
  PostReceiveNoteManualResponse,
  PostReceiveNoteManualResponseSchema,
  ReceiveNoteInfoSlice,
  ReceiveNoteStateSlice,
} from './types';

export const metadaWarehouseReceiveNoteApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getReceiveNoteState: build.query<ReceiveNoteStateSlice, GetReceiveNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v5/receive-note/${queryArg.receiveNoteId}`,
        queryArg,
      }),
      transformResponse: (response: GetReceiveNoteResponse) => sliceReceiveNoteState(response),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNoteState', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteRequestSchema,
        responseSchema: GetReceiveNoteResponseSchema,
      },
    }),
    getReceiveNoteInfo: build.query<ReceiveNoteInfoSlice, GetReceiveNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v5/receive-note/${queryArg.receiveNoteId}`,
        queryArg,
      }),
      transformResponse: (response: GetReceiveNoteResponse) => sliceReceiveNoteInfo(response),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNoteInfo', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteRequestSchema,
        responseSchema: GetReceiveNoteResponseSchema,
      },
    }),
    postReceiveNoteManual: build.mutation<
      PostReceiveNoteManualResponse,
      PostReceiveNoteManualRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v2/receive-note/manual`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: (result) => (result ? [...RECEIVE_NOTE_ALL_TAGS(result.receiveNoteId)] : []),
      extraOptions: {
        requestSchema: PostReceiveNoteManualRequestSchema,
        responseSchema: PostReceiveNoteManualResponseSchema,
      },
    }),
    patchReceiveNoteManual: build.mutation<
      PatchReceiveNoteManualResponse,
      PatchReceiveNoteManualRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v2/receive-note/${queryArg.receiveNoteId}/manual`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNoteInfo', id: queryArg.receiveNoteId},
        {type: 'deliveryNote', id: result?.deliveryNote.deliveryNoteId},
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteManualRequestSchema,
        responseSchema: PatchReceiveNoteManualResponseSchema,
      },
    }),
    patchReceiveNoteCompleted: build.mutation<
      PatchReceiveNoteCompletedResponse,
      PatchReceiveNoteCompletedRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/completed`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNoteState', id: queryArg.receiveNoteId},
        {type: 'receiveNoteInfo', id: queryArg.receiveNoteId},
        'deliveryNote',
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteCompletedRequestSchema,
        responseSchema: PatchReceiveNoteCompletedResponseSchema,
      },
    }),
    bulkDeleteReceiveNote: build.mutation<
      BulkDeleteReceiveNoteResponse,
      BulkDeleteReceiveNoteRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/receive-note/delete',
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: ['receiveNoteState', 'receiveNoteInfo', 'deliveryNote'],
      extraOptions: {
        requestSchema: BulkDeleteReceiveNoteRequestSchema,
        responseSchema: BulkDeleteReceiveNoteResponseSchema,
      },
    }),
    patchReceiveNoteState: build.mutation<
      PatchReceiveNoteStateResponse,
      PatchReceiveNoteStateRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/state`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNoteState', id: queryArg.receiveNoteId},
        {type: 'receiveNoteInfo', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteStateRequestSchema,
        responseSchema: PatchReceiveNoteStateResponseSchema,
      },
    }),
    patchReceiveNoteManualState: build.mutation<
      PatchReceiveNoteStateResponse,
      PatchReceiveNoteStateRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/manual/state`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNoteState' as const, id: queryArg.receiveNoteId},
        {type: 'receiveNoteInfo' as const, id: queryArg.receiveNoteId},
        {type: 'receiveNoteFastTrackItems' as const, id: queryArg.receiveNoteId},
        {type: 'receiveNoteFastTrackIssues' as const, id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteStateRequestSchema,
        responseSchema: PatchReceiveNoteStateResponseSchema,
      },
    }),
    getReceiveNoteFastTrackItems: build.query<
      NormalizedFastTrackItems,
      GetReceiveNoteFastTrackRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/fast-track`,
        queryArg,
      }),
      transformResponse: (response: GetReceiveNoteFastTrackResponse) =>
        normalizeFastTrackItems(response),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNoteFastTrackItems', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteFastTrackRequestSchema,
        responseSchema: GetReceiveNoteFastTrackResponseSchema,
      },
    }),
    getReceiveNoteFastTrackIssues: build.query<
      NormalizedFastTrackIssues,
      GetReceiveNoteFastTrackRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/fast-track`,
        queryArg,
      }),
      transformResponse: (response: GetReceiveNoteFastTrackResponse) =>
        normalizeFastTrackIssues(response),
      providesTags: (result, error, queryArg) => [
        {type: 'receiveNoteFastTrackIssues', id: queryArg.receiveNoteId},
      ],
      extraOptions: {
        requestSchema: GetReceiveNoteFastTrackRequestSchema,
        responseSchema: GetReceiveNoteFastTrackResponseSchema,
      },
    }),
    patchReceiveNoteFastTrack: build.mutation<PatchReceiveNoteResponse, PatchReceiveNoteRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/receive-note/${queryArg.receiveNoteId}/fast-track`,
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'receiveNoteFastTrackItems' as const, id: queryArg.receiveNoteId},
        {type: 'receiveNoteFastTrackIssues' as const, id: queryArg.receiveNoteId},
        'headerDynamicActions',
      ],
      extraOptions: {
        requestSchema: PatchReceiveNoteRequestSchema,
        responseSchema: PatchReceiveNoteResponseSchema,
      },
    }),
    patchReceiveNoteBulkEdit: build.mutation<
      PatchReceiveNoteBulkEditResponse,
      PatchReceiveNoteBulkEditRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/receive-note/bulk-edit',
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: ['receiveNoteState', 'receiveNoteInfo'],
      extraOptions: {
        requestSchema: PatchReceiveNoteBulkEditRequestSchema,
        responseSchema: PatchReceiveNoteBulkEditResponseSchema,
      },
    }),
  }),
});

export const {
  useGetReceiveNoteStateQuery,
  useGetReceiveNoteInfoQuery,
  usePostReceiveNoteManualMutation,
  usePatchReceiveNoteManualMutation,
  usePatchReceiveNoteCompletedMutation,
  useBulkDeleteReceiveNoteMutation,
  usePatchReceiveNoteStateMutation,
  usePatchReceiveNoteManualStateMutation,
  useGetReceiveNoteFastTrackItemsQuery,
  useGetReceiveNoteFastTrackIssuesQuery,
  usePatchReceiveNoteFastTrackMutation,
  usePatchReceiveNoteBulkEditMutation,
} = metadaWarehouseReceiveNoteApi;
